import React, { useState } from 'react';
import {
  Box,
  Button,
  FormHelperText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import {
  useAddStoreQuery,
  useUpdateStoreOwnerByIdQuery,
} from '../Query/AddStoreQuery';
import { useThemeColors } from '../../hooks/useThemeColors';
import allForm from '../Stateless/Form';
// import LocationSearchInputWrapper from '../Stateless/LocationSearchInputWrapper';
import {
  MerchantSchema,
  MerchantStripeDetailsSchemaPublishStatusEnum,
  MerchantPayPalSchemaPublishStatusEnum,
  MerchantNetBankingSchemaPublishStatusEnum,
} from '../../client-sdk';
import { toast } from 'react-hot-toast';
import LocationSearchInput from '../Stateless/LocationSearchInput';
import PhoneNumberInput from './Utils/PhoneNumberInput';
import { FieldInputProps, FormikProps } from 'formik';
import ImageUploadField from './Utils/ImageUploadField';
import { createCancellationPolicyValidationSchema } from '../HelperFunctions/validationUtils';
import {COLORS} from "../../theme/themeConstants"
interface FormValues {
  password?: string;
  confirmPassword?: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNo: string;
  currencyCode: string | undefined;
  businessType: string;
  commission: string;
  useCommonCommission: string;
  cancelStatus: string;
  cancellationPolicy: string;
  cancellationPolicyArabic: string;
  idProof: { file: File | null; url: string } | null;
  restaurantLicense: { file: File | null; url: string } | null;
  stripePublishStatus: string;
  stripeClientId: string;
  stripeSecretId: string;
  paypalPublishStatus: string;
  paypalClientId: string;
  paypalSecretId: string;
  netBankingPublishStatus: string;
  bankName: string;
  branch: string;
  accountNo: string;
  ifscCode: string;
  location: string;
  country: string;
  state: string;
  city: string;
}

interface AddStoreOwnerProps {
  onClose?: () => void;
  storeOwner?: MerchantSchema | null;
}

interface CustomComponentProps {
  field: {
    name: string;
    value: any;
    onChange: (e: any) => void;
    onBlur: () => void;
  };
  form: {
    setFieldValue: (field: string, value: any) => void;
    setFieldTouched: (field: string, touched: boolean) => void;
    setFieldError: (field: string, message: string | undefined) => void;
    values: FormValues;
    touched: Record<string, boolean>;
    errors: Record<string, any>;
  };
}

interface CustomButtonProps {
  values: FormValues;
  setFieldValue: (field: string, value: any) => void;
  setFieldTouched: (field: string, touched: boolean) => void;
  errors: Record<string, any>;
  touched: Record<string, boolean>;
  resetForm: () => void;
  handleSubmit: () => void;
}

interface FileValue {
  file: File;
  url: string;
}

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .required('Please enter the password')
    .min(8, 'Password must be at least 8 characters')
    .max(15, 'Password must not exceed 15 characters')
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/,
      'Password must contain at least one uppercase letter, one number, and one special character'
    )
    .matches(/^\S+$/, 'Password must not contain spaces'),
  confirmPassword: Yup.string()
    .required('Please enter the confirm password')
    .oneOf([Yup.ref('password'), ''], 'Passwords must match')
    .min(8, 'Password must be at least 8 characters')
    .max(15, 'Password must not exceed 15 characters')
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/,
      'Password must contain at least one uppercase letter, one number, and one special character'
    )
    .matches(/^\S+$/, 'Password must not contain spaces'),
  firstName: Yup.string()
  .max(100, 'Name must be at most 100 characters')
  .matches(/^[A-Za-z\s]+$/, 'Name should only contain letters and spaces')
  .required('First Name is required')
  .test(
    'not-only-spaces',
    'Name cannot be only spaces',
    (value) => typeof value === 'string' && value.trim().length > 0
  )
  .test(
    'no-leading-space',
    'Name cannot start with a space',
    (value) => typeof value === 'string' && !value.startsWith(' ')
  ),

lastName: Yup.string()
  .max(100, 'Name must be at most 100 characters')
  .matches(/^[A-Za-z\s]+$/, 'Name should only contain letters and spaces')
  .required('Last Name is required')
  .test(
    'not-only-spaces',
    'Name cannot be only spaces',
    (value) => typeof value === 'string' && value.trim().length > 0
  )
  .test(
    'no-leading-space',
    'Name cannot start with a space',
    (value) => typeof value === 'string' && !value.startsWith(' ')
  ),
  email: Yup.string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,3}$/,
        'Please enter a valid email address'
      )
      .required('Email is required'),
  mobileNo: Yup.string()
    .required('Please enter the mobile number')
    .matches(/^[0-9]+$/, 'Mobile number should contain only numbers')
    .test(
      'len',
      'Mobile number must be 9 digits or more',
      (val) => val?.length >= 9
    )
    .notOneOf(['0000000000'], 'Mobile number cannot be all zeroes'),
  commission: Yup.number().when('useCommonCommission', {
    is: 'Custom commission',
    then: (schema) =>
      schema
        .moreThan(0, 'Commission must be greater than 0')
        .max(100, 'Commission cannot exceed 100%')
        .required('Commission is required'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  cancelStatus: Yup.string().required('Cancel status is required'),
  cancellationPolicy: Yup.string().when('cancelStatus', {
    is: 'Allow',
    then: () => createCancellationPolicyValidationSchema('Cancellation Policy'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  cancellationPolicyArabic: Yup.string().when('cancelStatus', {
    is: 'Allow',
    then: () =>
      createCancellationPolicyValidationSchema('Cancellation Policy (Arabic)'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  accountNo: Yup.string().when('netBankingPublishStatus', {
    is: 'publish',
    then: (schema) =>
      schema
        .required('Account number is required when Net Banking is published')
        .test(
          'not-only-spaces',
          'Account number cannot be only spaces',
          (value) => typeof value === 'string' && value.trim().length > 0
        )
        .test(
          'no-leading-trailing-spaces',
          'Account number cannot start or end with spaces',
          (value) => typeof value === 'string' && !value.startsWith(' ') && !value.endsWith(' ')
        )
        .test(
          'valid-account-number',
          'Account number must contain only digits',
          (value) => {
            if (!value || typeof value !== 'string') return false;
            const trimmedValue = value.trim();
            return /^\d+$/.test(trimmedValue);
          }
        )
        .min(5, 'Account number must be at least 5 digits')
        .max(34, 'Account number cannot exceed 34 digits'),
  }),
  ifscCode: Yup.string().when('netBankingPublishStatus', {
    is: 'publish',
    then: (schema) =>
      schema
        .required('IFSC code is required when Net Banking is published')
        .test(
          'not-only-spaces',
          'IFSC code cannot be only spaces',
          (value) => typeof value === 'string' && value.trim().length > 0
        )
        .test(
          'no-leading-trailing-spaces',
          'IFSC code cannot start or end with spaces',
          (value) => typeof value === 'string' && !value.startsWith(' ') && !value.endsWith(' ')
        )
        .test(
          'valid-ifsc-format',
          'IFSC code must be 11 characters long and follow the format: AAAA0XXXXXX',
          (value) => {
            if (!value || typeof value !== 'string') return false;
            const trimmedValue = value.trim();
            return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(trimmedValue);
          }
        )
        .length(11, 'IFSC code must be exactly 11 characters'),
  }),
  idProof: Yup.mixed()
    .required('Id Proof is required')
    .test('fileSize', 'File size must be less than 500KB', function (value) {
      const fileValue = value as FileValue | null;
      if (!fileValue || !fileValue.file) return true;
      return fileValue.file.size <= 500000;
    })
    .test(
      'dimensions',
      'Image dimensions must be between 300x300px and 500x500px',
      function (value) {
        const fileValue = value as FileValue | null;
        if (!fileValue || !fileValue.file) return true;
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.src = e.target?.result as string;
            img.onload = () => {
              const { width, height } = img;
              resolve(
                width >= 300 && height >= 300 && width <= 500 && height <= 500
              );
            };
            img.onerror = () => resolve(false);
          };
          reader.readAsDataURL(fileValue.file);
        });
      }
    ),
  restaurantLicense: Yup.mixed()
    .required('Restaurant License is required')
    .test('fileSize', 'File size must be less than 500KB', function (value) {
      const fileValue = value as FileValue | null;
      if (!fileValue || !fileValue.file) return true;
      return fileValue.file.size <= 500000;
    })
    .test(
      'dimensions',
      'Image dimensions must be between 300x300px and 500x500px',
      function (value) {
        const fileValue = value as FileValue | null;
        if (!fileValue || !fileValue.file) return true;
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.src = e.target?.result as string;
            img.onload = () => {
              const { width, height } = img;
              resolve(
                width >= 300 && height >= 300 && width <= 500 && height <= 500
              );
            };
            img.onerror = () => resolve(false);
          };
          reader.readAsDataURL(fileValue.file);
        });
      }
    ),
  location: Yup.string().required('location is required')
    .test(
      'not-only-spaces',
      'Location cannot be only spaces',
      (value) => typeof value === 'string' && value.trim().length > 0
    ),
  country: Yup.string().required('Country is required'),
  state: Yup.string().required('State is required'),
  city: Yup.string().required('City is required'),
  stripeClientId: Yup.string().when('stripePublishStatus', {
    is: 'publish',
    then: (schema) =>
      schema
        .required('Client ID is required when Stripe is published')
        .test(
          'not-only-spaces',
          'Client ID cannot be only spaces',
          (value) => typeof value === 'string' && value.trim().length > 0
        )
        .test(
          'no-leading-trailing-spaces',
          'Client ID cannot start or end with spaces',
          (value) => typeof value === 'string' && !value.startsWith(' ') && !value.endsWith(' ')
        )
        .test(
          'valid-stripe-client-id',
          'Invalid Stripe Client ID format. Must start with "pk_" or "sk_" and contain only alphanumeric characters, hyphens, and underscores',
          (value) => {
            if (!value || typeof value !== 'string') return false;
            const trimmedValue = value.trim();
            // Stripe client IDs typically start with 'pk_' for publishable keys or 'sk_' for secret keys
            return /^(pk_|sk_)[a-zA-Z0-9_-]+$/.test(trimmedValue) && trimmedValue.length >= 10;
          }
        )
        .min(10, 'Client ID must be at least 10 characters')
        .max(100, 'Client ID cannot exceed 100 characters'),
  }),
  stripeSecretId: Yup.string().when('stripePublishStatus', {
    is: 'publish',
    then: (schema) =>
      schema
        .required('Secret ID is required when Stripe is published')
        .test(
          'not-only-spaces',
          'Secret ID cannot be only spaces',
          (value) => typeof value === 'string' && value.trim().length > 0
        )
        .test(
          'no-leading-trailing-spaces',
          'Secret ID cannot start or end with spaces',
          (value) => typeof value === 'string' && !value.startsWith(' ') && !value.endsWith(' ')
        )
        .test(
          'valid-stripe-secret-id',
          'Invalid Stripe Secret ID format. Must start with "sk_" and contain only alphanumeric characters, hyphens, and underscores',
          (value) => {
            if (!value || typeof value !== 'string') return false;
            const trimmedValue = value.trim();
            // Stripe secret keys must start with 'sk_' and contain alphanumeric characters
            return /^sk_[a-zA-Z0-9_-]+$/.test(trimmedValue) && trimmedValue.length >= 20;
          }
        )
        .min(20, 'Secret ID must be at least 20 characters')
        .max(150, 'Secret ID cannot exceed 150 characters'),
  }),
  paypalClientId: Yup.string().when('paypalPublishStatus', {
    is: 'publish',
    then: (schema) =>
      schema
        .required('Client ID is required when PayPal is published')
        .test(
          'not-only-spaces',
          'Client ID cannot be only spaces',
          (value) => typeof value === 'string' && value.trim().length > 0
        )
        .test(
          'no-leading-trailing-spaces',
          'Client ID cannot start or end with spaces',
          (value) => typeof value === 'string' && !value.startsWith(' ') && !value.endsWith(' ')
        )
        .test(
          'valid-paypal-client-id',
          'Invalid PayPal Client ID format',
          (value) => {
            if (!value || typeof value !== 'string') return false;
            const trimmedValue = value.trim();
            // PayPal client IDs are typically alphanumeric with some special characters
            return /^[a-zA-Z0-9_-]+$/.test(trimmedValue) && trimmedValue.length >= 8;
          }
        )
        .min(8, 'Client ID must be at least 8 characters')
        .max(100, 'Client ID cannot exceed 100 characters'),
  }),
  paypalSecretId: Yup.string().when('paypalPublishStatus', {
    is: 'publish',
    then: (schema) =>
      schema
        .required('Secret ID is required when PayPal is published')
        .test(
          'not-only-spaces',
          'Secret ID cannot be only spaces',
          (value) => typeof value === 'string' && value.trim().length > 0
        )
        .test(
          'no-leading-trailing-spaces',
          'Secret ID cannot start or end with spaces',
          (value) => typeof value === 'string' && !value.startsWith(' ') && !value.endsWith(' ')
        )
        .test(
          'valid-paypal-secret-id',
          'Invalid PayPal Secret ID format',
          (value) => {
            if (!value || typeof value !== 'string') return false;
            const trimmedValue = value.trim();
            // PayPal secret keys are typically alphanumeric with some special characters
            return /^[a-zA-Z0-9_-]+$/.test(trimmedValue) && trimmedValue.length >= 10;
          }
        )
        .min(10, 'Secret ID must be at least 10 characters')
        .max(150, 'Secret ID cannot exceed 150 characters'),
  }),
  bankName: Yup.string().when('netBankingPublishStatus', {
    is: 'publish',
    then: (schema) =>
      schema
        .required('Bank name is required when Net Banking is published')
        .test(
          'not-only-spaces',
          'Bank name cannot be only spaces',
          (value) => typeof value === 'string' && value.trim().length > 0
        )
        .test(
          'no-leading-trailing-spaces',
          'Bank name cannot start or end with spaces',
          (value) => typeof value === 'string' && !value.startsWith(' ') && !value.endsWith(' ')
        )
        .test(
          'valid-bank-name',
          'Bank name should only contain letters, spaces, and common punctuation',
          (value) => {
            if (!value || typeof value !== 'string') return false;
            const trimmedValue = value.trim();
            // Bank names typically contain letters, spaces, dots, and hyphens
            return /^[A-Za-z\s\.\-&]+$/.test(trimmedValue) && trimmedValue.length >= 2;
          }
        )
        .min(2, 'Bank name must be at least 2 characters')
        .max(100, 'Bank name cannot exceed 100 characters'),
  }),
  branch: Yup.string().when('netBankingPublishStatus', {
    is: 'publish',
    then: (schema) =>
      schema
        .required('Branch is required when Net Banking is published')
        .test(
          'not-only-spaces',
          'Branch cannot be only spaces',
          (value) => typeof value === 'string' && value.trim().length > 0
        )
        .test(
          'no-leading-trailing-spaces',
          'Branch cannot start or end with spaces',
          (value) => typeof value === 'string' && !value.startsWith(' ') && !value.endsWith(' ')
        )
        .test(
          'valid-branch-name',
          'Branch name should only contain letters, spaces, and common punctuation',
          (value) => {
            if (!value || typeof value !== 'string') return false;
            const trimmedValue = value.trim();
            // Branch names typically contain letters, spaces, dots, and hyphens
            return /^[A-Za-z\s\.\-&]+$/.test(trimmedValue) && trimmedValue.length >= 2;
          }
        )
        .min(2, 'Branch must be at least 2 characters')
        .max(100, 'Branch cannot exceed 100 characters'),
  }),
});


const AddStoreOwner: React.FC<AddStoreOwnerProps> = ({
  onClose,
  storeOwner,
}) => {
  const [inputKey, setInputKey] = useState(0);
  const [address, setAddress] = useState('');
  const navigate = useNavigate();
  const { mutate: addStoreOwner, isPending: isAdding } = useAddStoreQuery();
  const { mutate: updateStoreOwner, isPending: isUpdating } =
    useUpdateStoreOwnerByIdQuery();
  const { primary, warning } = useThemeColors();

  const [selectedCountryCode, setSelectedCountryCode] = useState('+962');

  const isPending = storeOwner ? isUpdating : isAdding;
  const initialValues: FormValues = {
    firstName: storeOwner?.firstName || '',
    lastName: storeOwner?.lastName || '',
    email: storeOwner?.email || '',
    mobileNo: storeOwner?.mobileNo ? storeOwner.mobileNo.slice(selectedCountryCode?.length) : '',
    password: '',
    confirmPassword: '',
    currencyCode: storeOwner?.currencyCode || 'JOD',
    businessType: storeOwner?.businessType || 'Restaurant',
    commission: storeOwner?.commissionPercentage?.toString() || '',
    useCommonCommission: storeOwner?.businessType || 'Use Common commission',
    cancelStatus: storeOwner?.cancelStatus ? 'Allow' : 'Not Allow',
    cancellationPolicy: storeOwner?.cancellationPolicyEnglish || '',
    cancellationPolicyArabic: storeOwner?.cancellationPolicyArabic || '',
    idProof: storeOwner?.idProof
      ? { file: null, url: storeOwner.idProof }
      : null,
    restaurantLicense: storeOwner?.restaurantLicense
      ? { file: null, url: storeOwner.restaurantLicense }
      : null,
    stripePublishStatus:
      storeOwner?.paymentDetails?.stripe?.publishStatus ===
        MerchantStripeDetailsSchemaPublishStatusEnum.Publish
        ? 'publish'
        : 'unpublish',
    stripeClientId: storeOwner?.paymentDetails?.stripe?.clientId || '',
    stripeSecretId: storeOwner?.paymentDetails?.stripe?.secretId || '',
    paypalPublishStatus:
      storeOwner?.paymentDetails?.paypal?.publishStatus ===
        MerchantPayPalSchemaPublishStatusEnum.Publish
        ? 'publish'
        : 'unpublish',
    paypalClientId: storeOwner?.paymentDetails?.paypal?.clientId || '',
    paypalSecretId: storeOwner?.paymentDetails?.paypal?.secretId || '',
    netBankingPublishStatus:
      storeOwner?.paymentDetails?.netBanking?.publishStatus ===
        MerchantNetBankingSchemaPublishStatusEnum.Publish
        ? 'publish'
        : 'unpublish',
    bankName: storeOwner?.paymentDetails?.netBanking?.bankName || '',
    branch: storeOwner?.paymentDetails?.netBanking?.branch || '',
    accountNo: storeOwner?.paymentDetails?.netBanking?.accountNumber || '',
    ifscCode: storeOwner?.paymentDetails?.netBanking?.ifscode || '',
    location: storeOwner?.address?.location || '',
    country: storeOwner?.address?.country || '',
    state: storeOwner?.address?.state || '',
    city: storeOwner?.address?.city || '',
  };

  const handleSubmit = async (values: FormValues) => {
    const sendToServer = {
      id: storeOwner?.id || '64e4b7f2d7c8ab1f8b2f9c31',
      createdUserId: '64e4b7f2d7c8ab1f8b2f9c31',
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      mobileNo: `${selectedCountryCode}${values.mobileNo}`,
      password: values.password,
      confirmPassword: values.confirmPassword,
      currencyCode: 'JOD',
      businessType: values.businessType,
      commissionPercentage: values.useCommonCommission === 'Use Common commission' ? 0 : Number(values.commission),
      cancelStatus: values.cancelStatus === 'Allow',
      cancellationPolicyEnglish: values.cancellationPolicy,
      cancellationPolicyArabic: values.cancellationPolicyArabic,
      idProof: typeof values.idProof === 'string' ? values.idProof : values.idProof?.url || '',
      restaurantLicense: typeof values.restaurantLicense === 'string' ? values.restaurantLicense : values.restaurantLicense?.url || '',
      address: {
        location: values.location,
        country: values.country,
        state: values.state,
        city: values.city,
      },
      paymentDetails: {
        stripe: {
          publishStatus:
            values.stripePublishStatus === 'publish'
              ? MerchantStripeDetailsSchemaPublishStatusEnum.Publish
              : MerchantStripeDetailsSchemaPublishStatusEnum.Unpublish,
          clientId: values.stripeClientId,
          secretId: values.stripeSecretId,
        },
        paypal: {
          publishStatus:
            values.paypalPublishStatus === 'publish'
              ? MerchantPayPalSchemaPublishStatusEnum.Publish
              : MerchantPayPalSchemaPublishStatusEnum.Unpublish,
          clientId: values.paypalClientId,
          secretId: values.paypalSecretId,
        },
        netBanking: {
          publishStatus:
            values.netBankingPublishStatus === 'publish'
              ? MerchantNetBankingSchemaPublishStatusEnum.Publish
              : MerchantNetBankingSchemaPublishStatusEnum.Unpublish,
          bankName: values.bankName,
          branch: values.branch,
          accountNumber: values.accountNo,
          ifscode: values.ifscCode,
        },
      },
      isActive: storeOwner?.isActive ?? true,
    };

    try {
      if (storeOwner?.id) {
        await updateStoreOwner(
          { id: storeOwner.id, data: sendToServer as any },
          {
            onSuccess: () => {
              toast.success('Store owner updated successfully');
              if (onClose) {
                onClose();
              } else {
                navigate('/store-owners');
              }
            },
            onError: (error: any) => {
              console.error('Error updating store owner:', error);
              toast.error(
                error.response?.data?.message || 'Failed to update store owner'
              );
            },
          }
        );
      } else {
        await addStoreOwner(sendToServer as any, {
          onSuccess: () => {
            toast.success('Store owner added successfully');
            if (onClose) {
              onClose();
            } else {
              navigate('/store-owners');
            }
          },
          onError: (error: any) => {
            console.error('Error adding store owner:', error);
            toast.error(
              error.response?.data?.message || 'Failed to add store owner'
            );
          },
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const allFields = [
    {
      name: 'firstName',
      label: 'First Name',
      type: 'text',
      isRequired: true,
    },
    {
      name: 'lastName',
      label: 'Last Name',
      type: 'text',
      isRequired: true,
    },
    {
      name: 'email',
      label: 'Email',
      type: 'text',
      isRequired: true,
    },
    {
      name: 'mobileNo',
      label: 'Mobile Number',
      type: 'custom',
      isRequired: true,
      component: ({
        field,
        form,
      }: {
        field: FieldInputProps<any>;
        form: FormikProps<any>;
      }) => (
        <PhoneNumberInput
          field={field}
          form={form}
          selectedCountryCode={selectedCountryCode}
          onCountryCodeChange={setSelectedCountryCode}
        />
      )
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      isRequired: true,
    },
    {
      name: 'confirmPassword',
      label: 'Confirm Password',
      type: 'password',
      isRequired: true,
    },
    {
      name: 'currencyCode',
      label: 'Currency Code',
      type: 'text',
      value: 'JOD',
      disabled: true,
    },
    {
      name: 'businessType',
      label: 'Business Type',
      type: 'radio',
      radioOptions: ['Restaurant', 'GroceryStore', 'PharmacyStore'],
      isRequired: true,
    },
    {
      name: 'useCommonCommission',
      label: 'Use Common commission',
      type: 'radio',
      radioOptions: ['Use Common commission', 'Custom commission'],
    },
    {
      name: 'commission',
      label: 'Commission In (%)',
      type: 'number',
      isRequired: true,
      dependsOn: { field: 'useCommonCommission', value: 'Custom commission' },
    },
    {
      name: 'cancelStatus',
      label: 'Cancel Status',
      type: 'radio',
      radioOptions: ['Allow', 'Not Allow'],
      isRequired: true,
    },
    {
      name: 'cancellationPolicy',
      label: 'Cancellation Policy',
      type: 'text',
      isRequired: true,
      dependsOn: { field: 'cancelStatus', value: 'Allow' },
      component: ({ field }: CustomComponentProps) => (
        <textarea
          {...field}
          rows={4}
          style={{
            width: '100%',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #ccc',
          }}
        />
      ),
    },
    {
      name: 'cancellationPolicyArabic',
      label: 'Cancellation Policy (In Arabic)',
      type: 'text',
      isRequired: true,
      dependsOn: { field: 'cancelStatus', value: 'Allow' },
      component: ({ field }: CustomComponentProps) => (
        <textarea
          {...field}
          rows={4}
          style={{
            width: '100%',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #ccc',
          }}
        />
      ),
    },
    {
      name: 'idProof',
      label: 'ID Proof',
      type: 'file',
      isRequired: true,
      component: () => (
        <ImageUploadField
          name="idProof"
          label="IDProof"
        />
      )
    },
    {
      name: 'restaurantLicense',
      label: 'Restaurant License',
      type: 'file',
      isRequired: true,
      component: () => (
        <ImageUploadField
          name="restaurantLicense"
          label="idProof"
        />
      )
    },
    {
      name: 'stripeSection',
      type: 'section',
      sectionTitle: 'Stripe',
      sectionLine: true,
    },
    {
      name: 'stripePublishStatus',
      label: 'Publish Status',
      type: 'radio',
      radioOptions: ['publish', 'unpublish'],
    },
    {
      name: 'stripeClientId',
      label: 'Client ID',
      type: 'text',
      disabled: (values: FormValues) => values.stripePublishStatus === 'unpublish',
      sx: (values: FormValues) => values.stripePublishStatus === 'unpublish' ? {
        '& .MuiInputBase-root': {
          filter: 'blur(1px)',
          opacity: 0.6,
        }
      } : {},
      component: ({ field, form }: CustomComponentProps) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          
          // Real-time validation: prevent leading/trailing spaces and invalid characters
          let processedValue = value;
          
          // Remove leading/trailing spaces
          processedValue = processedValue.trim();
          
          // Only allow alphanumeric characters, hyphens, underscores, and the prefix
          const validPattern = /^[a-zA-Z0-9_-]*$/;
          if (!validPattern.test(processedValue) && processedValue !== '') {
            // Remove invalid characters
            processedValue = processedValue.replace(/[^a-zA-Z0-9_-]/g, '');
          }
          
                      // Allow typing but validate format
          
          if (value !== processedValue) {
            form.setFieldValue(field.name, processedValue);
          } else {
            field.onChange(e);
          }
          form.setFieldTouched(field.name, true);
        };

        const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
          // Allow all printable characters - validation will be handled in handleChange
          const key = e.key;
          
          // Allow alphanumeric, underscore, hyphen, and common punctuation
          if (/^[a-zA-Z0-9_-]$/.test(key)) {
            return;
          }
          
          // Allow backspace, delete, arrow keys, etc.
          if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'].includes(key)) {
            return;
          }
          
          // Prevent other characters
          e.preventDefault();
        };

        const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);
        const isDisabled = form.values.stripePublishStatus === 'unpublish';
        const currentValue = field.value || '';

        return (
          <Box>
            <input
              {...field}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              onBlur={field.onBlur}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: hasError ? '1px solid #d32f2f' : '1px solid #ccc',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: isDisabled ? '#f5f5f5' : 'white',
                opacity: isDisabled ? 0.6 : 1,
                filter: isDisabled ? 'blur(1px)' : 'none',
              }}
              placeholder="Enter Stripe Client ID (e.g., pk_test_...)"
              disabled={isDisabled}
            />
            {currentValue && !currentValue.startsWith('pk_') && !currentValue.startsWith('sk_') && (
              <FormHelperText error style={{ fontSize: '12px', marginTop: '4px' }}>
                Client ID must start with "pk_" or "sk_"
              </FormHelperText>
            )}
          </Box>
        );
      },
    },
    {
      name: 'stripeSecretId',
      label: 'Secret ID',
      type: 'text',
      disabled: (values: FormValues) => values.stripePublishStatus === 'unpublish',
      sx: (values: FormValues) => values.stripePublishStatus === 'unpublish' ? {
        '& .MuiInputBase-root': {
          filter: 'blur(1px)',
          opacity: 0.6,
        }
      } : {},
      component: ({ field, form }: CustomComponentProps) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          
          // Real-time validation: prevent leading/trailing spaces and invalid characters
          let processedValue = value;
          
          // Remove leading/trailing spaces
          processedValue = processedValue.trim();
          
          // Only allow alphanumeric characters, hyphens, underscores, and the prefix
          const validPattern = /^[a-zA-Z0-9_-]*$/;
          if (!validPattern.test(processedValue) && processedValue !== '') {
            // Remove invalid characters
            processedValue = processedValue.replace(/[^a-zA-Z0-9_-]/g, '');
          }
          
                      // Allow typing but validate format
          
          if (value !== processedValue) {
            form.setFieldValue(field.name, processedValue);
          } else {
            field.onChange(e);
          }
          form.setFieldTouched(field.name, true);
        };

        const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
          // Allow all printable characters - validation will be handled in handleChange
          const key = e.key;
          
          // Allow alphanumeric, underscore, hyphen, and common punctuation
          if (/^[a-zA-Z0-9_-]$/.test(key)) {
            return;
          }
          
          // Allow backspace, delete, arrow keys, etc.
          if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'].includes(key)) {
            return;
          }
          
          // Prevent other characters
          e.preventDefault();
        };

        const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);
        const isDisabled = form.values.stripePublishStatus === 'unpublish';
        const currentValue = field.value || '';

        return (
          <Box>
            <input
              {...field}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              onBlur={field.onBlur}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: hasError ? '1px solid #d32f2f' : '1px solid #ccc',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: isDisabled ? '#f5f5f5' : 'white',
                opacity: isDisabled ? 0.6 : 1,
                filter: isDisabled ? 'blur(1px)' : 'none',
              }}
              placeholder="Enter Stripe Secret ID (e.g., sk_test_...)"
              disabled={isDisabled}
            />
            {currentValue && !currentValue.startsWith('sk_') && (
              <FormHelperText error style={{ fontSize: '12px', marginTop: '4px' }}>
                Secret ID must start with "sk_"
              </FormHelperText>
            )}
          </Box>
        );
      },
    },
    {
      name: 'paypalSection',
      type: 'section',
      sectionTitle: 'Paypal',
      sectionLine: true,
    },
    {
      name: 'paypalPublishStatus',
      label: 'Publish Status',
      type: 'radio',
      radioOptions: ['publish', 'unpublish'],
    },
    {
      name: 'paypalClientId',
      label: 'Client ID',
      type: 'text',
      disabled: (values: FormValues) => values.paypalPublishStatus === 'unpublish',
      sx: (values: FormValues) => values.paypalPublishStatus === 'unpublish' ? {
        '& .MuiInputBase-root': {
          filter: 'blur(1px)',
          opacity: 0.6,
        }
      } : {},
      component: ({ field, form }: CustomComponentProps) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          // Real-time validation: prevent leading/trailing spaces
          const trimmedValue = value.trim();
          if (value !== trimmedValue) {
            form.setFieldValue(field.name, trimmedValue);
          } else {
            field.onChange(e);
          }
          form.setFieldTouched(field.name, true);
        };

        const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);
        const isDisabled = form.values.paypalPublishStatus === 'unpublish';

        return (
          <Box>
            <input
              {...field}
              onChange={handleChange}
              onBlur={field.onBlur}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: hasError ? '1px solid #d32f2f' : '1px solid #ccc',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: isDisabled ? '#f5f5f5' : 'white',
                opacity: isDisabled ? 0.6 : 1,
                filter: isDisabled ? 'blur(1px)' : 'none',
              }}
              placeholder="Enter PayPal Client ID"
              disabled={isDisabled}
            />
          </Box>
        );
      },
    },
    {
      name: 'paypalSecretId',
      label: 'Secret ID',
      type: 'text',
      disabled: (values: FormValues) => values.paypalPublishStatus === 'unpublish',
      sx: (values: FormValues) => values.paypalPublishStatus === 'unpublish' ? {
        '& .MuiInputBase-root': {
          filter: 'blur(1px)',
          opacity: 0.6,
        }
      } : {},
      component: ({ field, form }: CustomComponentProps) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          // Real-time validation: prevent leading/trailing spaces
          const trimmedValue = value.trim();
          if (value !== trimmedValue) {
            form.setFieldValue(field.name, trimmedValue);
          } else {
            field.onChange(e);
          }
          form.setFieldTouched(field.name, true);
        };

        const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);
        const isDisabled = form.values.paypalPublishStatus === 'unpublish';

        return (
          <Box>
            <input
              {...field}
              onChange={handleChange}
              onBlur={field.onBlur}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: hasError ? '1px solid #d32f2f' : '1px solid #ccc',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: isDisabled ? '#f5f5f5' : 'white',
                opacity: isDisabled ? 0.6 : 1,
                filter: isDisabled ? 'blur(1px)' : 'none',
              }}
              placeholder="Enter PayPal Secret ID"
              disabled={isDisabled}
            />
          </Box>
        );
      },
    },
    {
      name: 'netBankingSection',
      type: 'section',
      sectionTitle: 'Net Banking',
      sectionLine: true,
    },
    {
      name: 'netBankingPublishStatus',
      label: 'Publish Status',
      type: 'radio',
      radioOptions: ['publish', 'unpublish'],
    },
    {
      name: 'bankName',
      label: 'Bank Name',
      type: 'text',
      disabled: (values: FormValues) => values.netBankingPublishStatus === 'unpublish',
      sx: (values: FormValues) => values.netBankingPublishStatus === 'unpublish' ? {
        '& .MuiInputBase-root': {
          filter: 'blur(1px)',
          opacity: 0.6,
        }
      } : {},
      component: ({ field, form }: CustomComponentProps) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          // Real-time validation: prevent leading/trailing spaces
          const trimmedValue = value.trim();
          if (value !== trimmedValue) {
            form.setFieldValue(field.name, trimmedValue);
          } else {
            field.onChange(e);
          }
          form.setFieldTouched(field.name, true);
        };

        const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);
        const isDisabled = form.values.netBankingPublishStatus === 'unpublish';

        return (
          <Box>
            <input
              {...field}
              onChange={handleChange}
              onBlur={field.onBlur}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: hasError ? '1px solid #d32f2f' : '1px solid #ccc',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: isDisabled ? '#f5f5f5' : 'white',
                opacity: isDisabled ? 0.6 : 1,
                filter: isDisabled ? 'blur(1px)' : 'none',
              }}
              placeholder="Enter Bank Name"
              disabled={isDisabled}
            />
          </Box>
        );
      },
    },
    {
      name: 'branch',
      label: 'Branch',
      type: 'text',
      disabled: (values: FormValues) => values.netBankingPublishStatus === 'unpublish',
      sx: (values: FormValues) => values.netBankingPublishStatus === 'unpublish' ? {
        '& .MuiInputBase-root': {
          filter: 'blur(1px)',
          opacity: 0.6,
        }
      } : {},
      component: ({ field, form }: CustomComponentProps) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          // Real-time validation: prevent leading/trailing spaces
          const trimmedValue = value.trim();
          if (value !== trimmedValue) {
            form.setFieldValue(field.name, trimmedValue);
          } else {
            field.onChange(e);
          }
          form.setFieldTouched(field.name, true);
        };

        const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);
        const isDisabled = form.values.netBankingPublishStatus === 'unpublish';

        return (
          <Box>
            <input
              {...field}
              onChange={handleChange}
              onBlur={field.onBlur}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: hasError ? '1px solid #d32f2f' : '1px solid #ccc',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: isDisabled ? '#f5f5f5' : 'white',
                opacity: isDisabled ? 0.6 : 1,
                filter: isDisabled ? 'blur(1px)' : 'none',
              }}
              placeholder="Enter Branch Name"
              disabled={isDisabled}
            />
          </Box>
        );
      },
    },
    {
      name: 'accountNo',
      label: 'Account No.',
      type: 'number',
      disabled: (values: FormValues) => values.netBankingPublishStatus === 'unpublish',
      sx: (values: FormValues) => values.netBankingPublishStatus === 'unpublish' ? {
        '& .MuiInputBase-root': {
          filter: 'blur(1px)',
          opacity: 0.6,
        }
      } : {},
      component: ({ field, form }: CustomComponentProps) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          // Real-time validation: only allow digits
          const digitsOnly = value.replace(/\D/g, '');
          if (value !== digitsOnly) {
            form.setFieldValue(field.name, digitsOnly);
          } else {
            field.onChange(e);
          }
          form.setFieldTouched(field.name, true);
        };

        const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);
        const isDisabled = form.values.netBankingPublishStatus === 'unpublish';

        return (
          <Box>
            <input
              {...field}
              onChange={handleChange}
              onBlur={field.onBlur}
              type="text"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: hasError ? '1px solid #d32f2f' : '1px solid #ccc',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: isDisabled ? '#f5f5f5' : 'white',
                opacity: isDisabled ? 0.6 : 1,
                filter: isDisabled ? 'blur(1px)' : 'none',
              }}
              placeholder="Enter Account Number (digits only)"
              disabled={isDisabled}
            />
          </Box>
        );
      },
    },
    {
      name: 'ifscCode',
      label: 'IFSC Code',
      type: 'text',
      disabled: (values: FormValues) => values.netBankingPublishStatus === 'unpublish',
      sx: (values: FormValues) => values.netBankingPublishStatus === 'unpublish' ? {
        '& .MuiInputBase-root': {
          filter: 'blur(1px)',
          opacity: 0.6,
        }
      } : {},
      component: ({ field, form }: CustomComponentProps) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          // Real-time validation: convert to uppercase and allow only alphanumeric
          const upperValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
          if (value !== upperValue) {
            form.setFieldValue(field.name, upperValue);
          } else {
            field.onChange(e);
          }
          form.setFieldTouched(field.name, true);
        };

        const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);
        const isDisabled = form.values.netBankingPublishStatus === 'unpublish';

        return (
          <Box>
            <input
              {...field}
              onChange={handleChange}
              onBlur={field.onBlur}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: hasError ? '1px solid #d32f2f' : '1px solid #ccc',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: isDisabled ? '#f5f5f5' : 'white',
                opacity: isDisabled ? 0.6 : 1,
                filter: isDisabled ? 'blur(1px)' : 'none',
              }}
              placeholder="Enter IFSC Code (e.g., SBIN0001234)"
              disabled={isDisabled}
              maxLength={11}
            />
          </Box>
        );
      },
    },
    {
      name: 'addressSection',
      type: 'section',
      sectionTitle: 'Address',
      sectionLine: true,
    },
    {
      name: 'location',
      label: 'Location',
      type: 'custom',
      isRequired: true,
      // component: LocationSearchInputWrapper,
      component: ({ field, form }: any) => {
        const handleSearchLocation = (data: {
          latLng: { lat: number; lng: number };
          city: string;
          state: string;
          country: string;
          formattedAddress?: string;
        }) => {
          const cityValue = data.city && data.city.trim() ? data.city : data.state;
          // Use the full formatted address if available, otherwise fall back to city, state, country
          const locationString = data.formattedAddress || `${cityValue}, ${data.state}, ${data.country}`;
          form.setFieldValue(field.name, locationString);
          form.setFieldTouched(field.name, false);
          form.setFieldValue('city', cityValue);
          form.setFieldTouched('city', false);
          form.setFieldValue('state', data.state);
          form.setFieldTouched('state', false);
          form.setFieldValue('country', data.country);
          form.setFieldTouched('country', false);
          setTimeout(() => {
            form.validateForm();
          }, 0);
        };

        const hasError =
          form.touched[field.name] && Boolean(form.errors[field.name]);

        return (
          <Box>
            <LocationSearchInput
              key={inputKey} // re-initialize component on reset
              handlesearchLocation={handleSearchLocation}
              address={address}
              // setAddress={setAddress}
              setAddress={(val) => {
                setAddress(val);
                form.setFieldValue(field.name, val);
                form.setFieldTouched(field.name, true, false); // Always mark as touched on change/erase
              }}
            />
            {hasError && (
              <FormHelperText error>{form.errors[field.name]}</FormHelperText>
            )}
          </Box>
        );
      },
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      isRequired: true,
      disabled: true,
    },
    {
      name: 'state',
      label: 'State',
      type: 'text',
      isRequired: true,
      disabled: true,
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      isRequired: true,
      disabled: true,
    },
  ];

  const filteredFields = storeOwner
    ? allFields.filter(
      (field) => field.name !== 'password' && field.name !== 'confirmPassword'
    )
    : allFields;

  const formConfig = {
    initialValues,
    validationSchema,
    onSubmit: (values: Record<string, any>) => {
      const formValues = values as FormValues;
      return handleSubmit(formValues);
    },
    fields: filteredFields,
    customButton: ({ resetForm, handleSubmit }: CustomButtonProps) => (
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disabled={isPending}
          sx={{ bgcolor: primary.main, '&:hover': {
                      backgroundColor: primary.dark,
                    }, }}
          onClick={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button
          type="button"
          variant="outlined"
          onClick={() => {
            resetForm(); // Reset Formik
            setAddress(''); // Clear address state
            setInputKey((prev) => prev + 1); // Force re-render of location input
          }}
          sx={{
            textTransform: 'none',
            fontSize: 14,
            px: 3,
            py: 1,
            borderColor: COLORS.secondary.main,
            color: COLORS.secondary.main,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '&:disabled': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            },
          }}
        >
          Reset
        </Button>

        <Button
          variant="contained"
          sx={{ bgcolor: warning.main }}
          onClick={() => (onClose ? onClose() : navigate(-1))}
          type="button"
        >
          Cancel
        </Button>
      </Box>
    ),
  };

  return allForm(formConfig)();
};

export default AddStoreOwner;
